// PATH: src/components/modules/devtools/lab/panels/LabPanel.js

import React, { useMemo, useState, useEffect } from "react";
import { Box, Button, Paper, Typography, TextField, Divider, Alert, Checkbox, FormControlLabel } from "@mui/material";
import { labCreateRun, labGetRun, labListRuns, labRunAllSteps, labRunStep, labApplyPatch, labScanContext } from "../api/labApi";

function ArtifactBlock({ artifact }) {
  if (!artifact) return null;
  const title = artifact.artifact_type || "artifact";
  let body = artifact.content_json;

  let displayText = "";
  try {
    const parsed = typeof body === "string" ? JSON.parse(body) : body;
    displayText = JSON.stringify(parsed, null, 2);
  } catch (e) {
    displayText = String(body ?? "");
  }

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{displayText}</pre>
    </Paper>
  );
}

export default function LabPanel() {
  const [goalText, setGoalText] = useState("");
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [applyMsg, setApplyMsg] = useState("");

  const [applyBackend, setApplyBackend] = useState(true);
  const [applyFrontend, setApplyFrontend] = useState(false);
  const [runSql, setRunSql] = useState(false);
  const [targetProjectId, setTargetProjectId] = useState("microtasks_sandbox");

  const artifacts = useMemo(() => selectedRun?.artifacts || [], [selectedRun]);

  const latestReview = useMemo(() => {
    const review = [...artifacts].reverse().find((a) => a.artifact_type === "review");
    if (!review) return null;
    try {
      return JSON.parse(review.content_json);
    } catch {
      return null;
    }
  }, [artifacts]);

  const isApproved = Boolean(latestReview?.approved);

  async function refreshRuns() {
    try {
      setError("");
      const data = await labListRuns({ limit: 50 });
      setRuns(Array.isArray(data?.runs) ? data.runs : []);
    } catch (e) {
      setError(String(e?.message || e));
    }
  }

  async function loadRun(runId) {
    try {
      setError("");
      const data = await labGetRun(runId);
      setSelectedRun(data?.run || null);
    } catch (e) {
      setError(String(e?.message || e));
    }
  }

  useEffect(() => {
    refreshRuns();
  }, []);

  useEffect(() => {
    if (selectedRunId) loadRun(selectedRunId);
  }, [selectedRunId]);

  async function handleCreateRun() {
    if (!goalText.trim()) return;
    setLoading(true);
    try {
      setError("");
      setApplyMsg("");
      const resp = await labCreateRun({ goal_text: goalText.trim() });
      const runId = resp?.run_id;
      setGoalText("");
      await refreshRuns();
      if (runId) {
        setSelectedRunId(runId);
        await loadRun(runId);
      }
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  async function handleRunAll() {
    if (!selectedRunId) return;
    setLoading(true);
    try {
      setError("");
      setApplyMsg("");
      await labRunAllSteps(selectedRunId);
      await loadRun(selectedRunId);
      await refreshRuns();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  async function handleRunStep(step) {
    if (!selectedRunId) return;
    setLoading(true);
    try {
      setError("");
      setApplyMsg("");
      await labRunStep(selectedRunId, step);
      await loadRun(selectedRunId);
      await refreshRuns();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

async function handleScan() {
  if (!selectedRunId) return;
  setLoading(true);
  try {
    setError("");
    setApplyMsg("");
    await labScanContext(selectedRunId, { project_id: targetProjectId || null });
    await loadRun(selectedRunId);
    await refreshRuns();
  } catch (e) {
    setError(String(e?.message || e));
  } finally {
    setLoading(false);
  }
}


  async function handleApply() {
    if (!selectedRunId) return;
    setLoading(true);
    try {
      setError("");
      setApplyMsg("");
      const resp = await labApplyPatch(selectedRunId, {
        apply_backend: applyBackend,
        apply_frontend: applyFrontend,
        run_sql: runSql,
        target_project_id: targetProjectId || null,
      });
      setApplyMsg(`Applied. Wrote ${resp?.written_files?.length || 0} files. ran_sql=${resp?.ran_sql ? "yes" : "no"}`);
      await loadRun(selectedRunId);
      await refreshRuns();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ display: "flex", gap: 2, height: "100%", minHeight: 650 }}>
      <Paper elevation={2} sx={{ width: "30%", p: 2, overflow: "auto" }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Lab Runs
        </Typography>

        <TextField
          label="New goal"
          value={goalText}
          onChange={(e) => setGoalText(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
        <Button variant="contained" sx={{ mt: 1 }} onClick={handleCreateRun} disabled={loading || !goalText.trim()} fullWidth>
          Create Run
        </Button>

        <Divider sx={{ my: 2 }} />

        {runs.map((r) => (
          <Paper
            key={r.id}
            elevation={0}
            onClick={() => setSelectedRunId(r.id)}
            sx={{
              p: 1,
              mb: 1,
              cursor: "pointer",
              border: "1px solid rgba(0,0,0,0.12)",
              background: selectedRunId === r.id ? "rgba(25,118,210,0.08)" : "#fff",
            }}
          >
            <Typography variant="subtitle2">#{r.id} — {r.status || "unknown"}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {(r.goal_text || "").slice(0, 120)}
              {(r.goal_text || "").length > 120 ? "…" : ""}
            </Typography>
          </Paper>
        ))}
      </Paper>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">Lab Details</Typography>
          {selectedRun ? (
            <>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                <b>Run:</b> #{selectedRun.id} &nbsp; <b>Status:</b> {selectedRun.status}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                <b>Goal:</b> {selectedRun.goal_text}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                <Button variant="contained" onClick={handleRunAll} disabled={loading}>
                  Run Planner → Patcher → Reviewer
                </Button>
                <Button variant="outlined" onClick={() => handleRunStep("planner")} disabled={loading}>
                  Run Planner
                </Button>
                <Button variant="outlined" onClick={handleScan} disabled={loading}>
                  Scan Context
                </Button>
                <Button variant="outlined" onClick={() => handleRunStep("patcher")} disabled={loading}>
                  Run Patcher
                </Button>
                <Button variant="outlined" onClick={() => handleRunStep("reviewer")} disabled={loading}>
                  Run Reviewer
                </Button>
                <Button variant="outlined" onClick={() => handleRunStep("refine")} disabled={loading}>
                  Refine (Re-run Patcher)
                </Button>
              </Box>


{selectedRun?.context?.file_map && !selectedRun.context.file_map.has_scan && (
  <Alert severity="warning" sx={{ mt: 1 }}>
    No file scan found for this project yet. Planner may be ungrounded. Click <b>Scan Context</b> to build file/function maps.
  </Alert>
)}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2">Apply Patch (External App)</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mb: 1 }}>
                Target roots are resolved from devide_projects (by project_id). Fallback: LAB_TARGET_BACKEND_ROOT / LAB_TARGET_FRONTEND_ROOT env vars. Apply is enabled only when approved.
              
</Typography>

              <TextField
                label="Target project_id"
                value={targetProjectId}
                onChange={(e) => setTargetProjectId(e.target.value)}
                size="small"
                sx={{ mb: 1, maxWidth: 280 }}
              />

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                <FormControlLabel control={<Checkbox checked={applyBackend} onChange={(e) => setApplyBackend(e.target.checked)} />} label="Apply backend" />
                <FormControlLabel control={<Checkbox checked={applyFrontend} onChange={(e) => setApplyFrontend(e.target.checked)} />} label="Apply frontend" />
                <FormControlLabel control={<Checkbox checked={runSql} onChange={(e) => setRunSql(e.target.checked)} />} label="Run SQL" />
                <Button variant="contained" onClick={handleApply} disabled={loading || !isApproved}>
                  Apply
                </Button>
                {applyMsg ? <Typography variant="body2">{applyMsg}</Typography> : null}
              </Box>

              {!isApproved ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Not approved yet — re-run Planner/Patcher until Reviewer approves.
                </Alert>
              ) : null}
            </>
          ) : (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              Select a run to view artifacts.
            </Typography>
          )}
        </Paper>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {selectedRun?.artifacts?.map((a) => <ArtifactBlock key={a.id} artifact={a} />)}
      </Box>
    </Box>
  );
}
